import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { JjamesButtonComponent } from 'shared-lib';

export type ExportScope = 'view' | 'all';

export interface ExportScopeDialogData {
    /** Rows currently rendered — one page. */
    viewCount: number;
    /** Rows matching the active filters, across every page. */
    totalCount: number;
    /** Whether a status or stay filter is narrowing the list. */
    isFiltered: boolean;
    /** Ceiling on a single export, so "all" cannot mean an unbounded download. */
    maxRows: number;
}

/**
 * Asks which rows the CSV should contain before writing one.
 *
 * The export used to take the current page without saying so, which produced a file named
 * "payment-obligations-export" holding twenty of however many thousand rows exist — a partial export
 * that looks complete is worse than no export, because it gets forwarded to accounting. The choice is
 * only worth presenting when the two answers differ, so the caller skips this dialog when the page
 * already is everything.
 */
@Component({
    selector: 'app-export-scope-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatIconModule, JjamesButtonComponent, TranslatePipe],
    templateUrl: './export-scope-dialog.component.html'
})
export class ExportScopeDialogComponent {
    private dialogRef = inject(MatDialogRef<ExportScopeDialogComponent>);
    readonly data = inject<ExportScopeDialogData>(MAT_DIALOG_DATA);

    /**
     * True when the filtered set is larger than one export may carry.
     *
     * The dialog says so up front rather than letting the operator discover it from a row count in a
     * spreadsheet: a silently truncated export is indistinguishable from a complete one.
     */
    readonly exceedsLimit = computed(() => this.data.totalCount > this.data.maxRows);

    readonly exportedIfAll = computed(() => Math.min(this.data.totalCount, this.data.maxRows));

    choose(scope: ExportScope): void {
        this.dialogRef.close(scope);
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}
